import { useCallback, useEffect, useRef, useState } from "react";
import { getConversations, getInboxNavigation } from "@/api/conversationsApi";
import {
  fetchInboxConversations,
  fetchInboxNavigation,
  fetchInboxSearchConversations,
  inboxListPollIntervalMs,
  isHumanAgentInboxLive,
} from "@/api/inboxAgentApi";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { surfaceInboxHttpError } from "@/lib/inboxErrorToast";
import { applyInboxPollEventsToList } from "@/lib/applyInboxPollEventsToList";
import { subscribeInboxPollListEvents } from "@/lib/inboxPollListEvents";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Backoff for silently retrying transient 401/403 workspace-auth blips during
// inbox list load (same policy as useConversationThread / useWorkspaceDirectory).
const AUTH_RETRY_DELAYS_MS = [500, 1200, 2500];

function isAuthBlip(error) {
  const status =
    error && typeof error === "object" && "status" in error && typeof error.status === "number"
      ? error.status
      : undefined;
  return status === 401 || status === 403;
}

/** Live API accepts all / your-inbox / unassigned / bot-chats / team inbox UUID as `view`. */
function shouldFetchLiveConversationList(categoryId) {
  if (!categoryId || categoryId === "dashboard") return false;
  if (["all", "your-inbox", "unassigned", "bot-chats"].includes(categoryId)) return true;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(categoryId).trim());
}

function inboxViewKey(activeCategory, workspaceId, centralInbox, messageSearch) {
  return `${activeCategory}|${workspaceId ?? ""}|${centralInbox ? "central" : "scoped"}|${messageSearch ?? ""}`;
}

/**
 * Inbox list + navigation — uses the remote inbox API when `VITE_USE_HUMAN_AGENT_INBOX` is enabled.
 * Background poll ({@link inboxListPollIntervalMs}) always refreshes silently (no list skeleton).
 * Skeleton shows only on first load for a view or after switching inbox category/workspace.
 *
 * @param {string} activeCategory
 * @param {{ centralInbox?: boolean, searchQuery?: string }} [options]
 */
export function useInboxConversations(activeCategory, options = {}) {
  const { centralInbox = false, searchQuery = "" } = options;
  const { activeWorkspaceId } = useWorkspace();
  const [conversations, setConversations] = useState([]);
  const [inboxCategories, setInboxCategories] = useState([]);
  const [teamInboxes, setTeamInboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const conversationCountRef = useRef(0);
  const pagesLoadedRef = useRef(1);
  const loadingMoreRef = useRef(false);
  const conversationsRef = useRef([]);
  const unknownRefreshTimerRef = useRef(null);

  useEffect(() => {
    const trimmed = (searchQuery || "").trim();
    if (trimmed.length < 2) {
      setDebouncedSearch("");
      return undefined;
    }
    const timer = window.setTimeout(() => setDebouncedSearch(trimmed), 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const viewKey = inboxViewKey(activeCategory, activeWorkspaceId, centralInbox, debouncedSearch);

  // A refresh reloads only the first page. When the agent has paged further down,
  // `preserveExtraPages` keeps those extra rows below the reloaded page instead of
  // truncating the list under them.
  const applyRows = useCallback((rows, { preserveExtraPages = false } = {}) => {
    const keepTail = preserveExtraPages && pagesLoadedRef.current > 1;
    let next = rows;
    if (keepTail) {
      const firstPageIds = new Set(rows.map((r) => String(r.id)));
      next = rows.concat(
        conversationsRef.current.filter((c) => !firstPageIds.has(String(c.id))),
      );
    }
    setConversations(next);
    conversationsRef.current = next;
    conversationCountRef.current = next.length;
    return keepTail;
  }, []);

  const refresh = useCallback(
    async ({ silent = false, preserveExtraPages = false } = {}) => {
      const showSkeleton = !silent && conversationCountRef.current === 0;
      if (showSkeleton) {
        setLoading(true);
      }
      setError(null);

      let attempt = 0;
      for (;;) {
        try {
          if (isHumanAgentInboxLive()) {
            const scope = centralInbox ? "central" : undefined;
            const navPromise = fetchInboxNavigation({
              workspaceId: centralInbox ? null : activeWorkspaceId,
              scope,
            });
            const convPromise = shouldFetchLiveConversationList(activeCategory)
              ? debouncedSearch.length >= 2
                ? fetchInboxSearchConversations({
                    q: debouncedSearch,
                    view: activeCategory,
                    workspaceId: centralInbox ? null : activeWorkspaceId,
                    scope,
                  })
                : fetchInboxConversations({
                    view: activeCategory,
                    workspaceId: centralInbox ? null : activeWorkspaceId,
                    scope,
                  })
              : Promise.resolve({ data: [] });
            const [convSettled, navSettled] = await Promise.allSettled([convPromise, navPromise]);
            if (convSettled.status === "rejected") {
              throw convSettled.reason;
            }
            const rows = convSettled.value.data ?? [];
            if (!applyRows(rows, { preserveExtraPages })) {
              setNextCursor(convSettled.value.nextCursor ?? null);
              pagesLoadedRef.current = 1;
            }
            if (navSettled.status === "fulfilled") {
              setInboxCategories(navSettled.value.data?.inboxCategories ?? []);
              setTeamInboxes(navSettled.value.data?.teamInboxes ?? []);
            } else if (!isAuthBlip(navSettled.reason)) {
              surfaceInboxHttpError(navSettled.reason, { skipAuthRedirect: true });
            }
          } else {
            const [convRes, navRes] = await Promise.all([
              getConversations({ view: activeCategory }),
              getInboxNavigation(),
            ]);
            const rows = convRes.data ?? [];
            if (!applyRows(rows, { preserveExtraPages })) {
              setNextCursor(null);
              pagesLoadedRef.current = 1;
            }
            setInboxCategories(navRes.data?.inboxCategories ?? []);
            setTeamInboxes(navRes.data?.teamInboxes ?? []);
          }
          return;
        } catch (e) {
          if (isAuthBlip(e) && attempt < AUTH_RETRY_DELAYS_MS.length) {
            surfaceInboxHttpError(e, { skipAuthRedirect: true });
            await delay(AUTH_RETRY_DELAYS_MS[attempt]);
            attempt += 1;
            continue;
          }
          surfaceInboxHttpError(e, { skipAuthRedirect: silent });
          if (!isAuthBlip(e)) {
            setError(e instanceof Error ? e : new Error(String(e)));
          }
          return;
        } finally {
          if (showSkeleton) {
            setLoading(false);
          }
        }
      }
    },
    [activeCategory, activeWorkspaceId, centralInbox, debouncedSearch, applyRows],
  );

  const loadMore = useCallback(async () => {
    if (!isHumanAgentInboxLive() || !nextCursor || loadingMoreRef.current) return;
    if (!shouldFetchLiveConversationList(activeCategory)) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const scope = centralInbox ? "central" : undefined;
      const workspaceId = centralInbox ? null : activeWorkspaceId;
      const page =
        debouncedSearch.length >= 2
          ? await fetchInboxSearchConversations({
              q: debouncedSearch,
              view: activeCategory,
              workspaceId,
              scope,
              cursor: nextCursor,
            })
          : await fetchInboxConversations({
              view: activeCategory,
              workspaceId,
              scope,
              cursor: nextCursor,
            });
      const rows = page.data ?? [];
      setConversations((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        const merged = prev.concat(rows.filter((r) => !seen.has(r.id)));
        conversationCountRef.current = merged.length;
        conversationsRef.current = merged;
        return merged;
      });
      setNextCursor(page.nextCursor ?? null);
      pagesLoadedRef.current += 1;
    } catch (e) {
      surfaceInboxHttpError(e, { skipAuthRedirect: true });
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [activeCategory, activeWorkspaceId, centralInbox, debouncedSearch, nextCursor]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    const showSkeleton = conversationCountRef.current === 0;
    if (showSkeleton) {
      setLoading(true);
    }
    void refreshRef.current({ silent: true }).finally(() => {
      if (showSkeleton) {
        setLoading(false);
      }
    });
  }, [viewKey]);

  useEffect(() => {
    const pollMs = inboxListPollIntervalMs();
    if (pollMs <= 0 || debouncedSearch.length >= 2) return undefined;
    const id = window.setInterval(() => {
      if (document.hidden) return;
      if (loadingMoreRef.current) return;
      void refreshRef.current({ silent: true, preserveExtraPages: true });
    }, pollMs);
    return () => window.clearInterval(id);
  }, [debouncedSearch]);

  // The interval above is skipped while the tab is hidden, so catch up as soon as
  // the agent comes back rather than showing a stale list until the next tick.
  useEffect(() => {
    if (debouncedSearch.length >= 2) return undefined;
    const catchUp = () => {
      if (document.hidden || loadingMoreRef.current) return;
      void refreshRef.current({ silent: true, preserveExtraPages: true });
    };
    document.addEventListener("visibilitychange", catchUp);
    window.addEventListener("focus", catchUp);
    return () => {
      document.removeEventListener("visibilitychange", catchUp);
      window.removeEventListener("focus", catchUp);
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (debouncedSearch.length >= 2) return undefined;
    const UNKNOWN_REFRESH_DEBOUNCE_MS = 400;
    const unsub = subscribeInboxPollListEvents((payload) => {
      const { next, unknownSessionIds } = applyInboxPollEventsToList(
        conversationsRef.current,
        payload,
      );
      conversationsRef.current = next;
      conversationCountRef.current = next.length;
      setConversations(next);
      if (!unknownSessionIds.length) return;
      if (unknownRefreshTimerRef.current) {
        window.clearTimeout(unknownRefreshTimerRef.current);
      }
      unknownRefreshTimerRef.current = window.setTimeout(() => {
        unknownRefreshTimerRef.current = null;
        void refreshRef.current({ silent: true, preserveExtraPages: true });
      }, UNKNOWN_REFRESH_DEBOUNCE_MS);
    });
    return () => {
      unsub();
      if (unknownRefreshTimerRef.current) {
        window.clearTimeout(unknownRefreshTimerRef.current);
        unknownRefreshTimerRef.current = null;
      }
    };
  }, [debouncedSearch]);

  const patchConversation = useCallback((conversationId, updater) => {
    setConversations((prev) => {
      const next = prev.map((c) => (c.id === conversationId ? updater(c) : c));
      conversationsRef.current = next;
      return next;
    });
  }, []);

  return {
    conversations,
    inboxCategories,
    teamInboxes,
    loading,
    error,
    refresh,
    patchConversation,
    loadMore,
    loadingMore,
    hasMore: Boolean(nextCursor),
  };
}
